import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Tabs,
  Tab,
  Divider,
  Grid,
  Paper,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Button,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  DialogActions
} from "@mui/material";
import {
    Flag as FlagIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  ChangeHistory as ChangeRequestIcon,
  BarChart as ProgressIcon,
  CalendarMonth as CalendarIcon,
  SupervisorAccount as ManagerIcon,
  AccountTree as DepartmentIcon,
  AttachMoney as BudgetIcon,
  Edit as EditIcon,
  CurrencyRupee,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Assignment as TaskIcon,Description
} from "@mui/icons-material";
import axios from "axios";
import { IndianRupeeIcon } from "lucide-react";
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';

// API base URL - Make sure this matches your backend server
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// TabPanel component for tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`project-tabpanel-${index}`}
      aria-labelledby={`project-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// ProjectStatusChip component from AllProjectsDashboard
const ProjectStatusChip = ({ status }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'Not Started': return 'default';
      case 'Planning': return 'info';
      case 'In Progress': return 'primary';
      case 'Active': return 'primary';
      case 'On Hold': return 'warning';
      case 'Completed': return 'success';
      default: return 'default';
    }
  };

  return (
    <Chip 
      label={status} 
      color={getStatusColor(status)} 
      size="small" 
      sx={{ fontWeight: 'medium' }}
    />
  );
};

// Change Request Status Chip
// Change Request Status Chip - FIXED VERSION
const ChangeRequestStatusChip = ({ status }) => {
  const getStatusColor = (status) => {
    // Fix: Add null/undefined check and convert to string safely
    const statusStr = status ? String(status).toLowerCase() : '';
    
    switch (statusStr) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'in review': return 'info';
      case 'implemented': return 'success';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  return (
    <Chip 
      label={status || 'Unknown'} 
      color={getStatusColor(status)} 
      size="small" 
      sx={{ fontWeight: 'medium' }}
    />
  );
};

const ProjectDetailsDialog = ({ open, handleClose, projectId }) => {
  const [value, setValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [team, setTeam] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [error, setError] = useState(null);
  const [companyConfig, setCompanyConfig] = useState(null);
  const [changeRequestStats, setChangeRequestStats] = useState({ 
    total: 0, 
    approved: 0, 
    pending: 0, 
    rejected: 0,
    inReview: 0,
    implemented: 0
  });
  
  // Status update related states
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [status, setStatus] = useState('');
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [notes, setNotes] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    type: 'success'
  });

  // Status mapping
  const backendToFrontendStatus = {
    'Active': 'In Progress',
    'Planning': 'Planning',
    'Completed': 'Completed',
    'On Hold': 'On Hold',
    'Cancelled': 'On Hold'
  };

  const frontendToBackendStatus = {
    'Not Started': 'Active',
    'Planning': 'Planning',
    'In Progress': 'Active',
    'On Hold': 'On Hold',
    'Completed': 'Completed'
  };
const priorities = [
  { id: 1, name: "Highest", icon: <FlagIcon fontSize="small" style={{ color: "#FF5630" }} /> },
  { id: 2, name: "High", icon: <FlagIcon fontSize="small" style={{ color: "#FF8B00" }} /> },
  { id: 3, name: "Medium", icon: <FlagIcon fontSize="small" style={{ color: "#FFAB00" }} /> },
  { id: 4, name: "Low", icon: <FlagIcon fontSize="small" style={{ color: "#36B37E" }} /> },
  { id: 5, name: "Lowest", icon: <FlagIcon fontSize="small" style={{ color: "#57D9A3" }} /> },
  { id: 6, name: "Normal", icon: <FlagIcon fontSize="small" style={{ color: "#FF5630" }} /> },
  { id: 7, name: "P1", icon: <FlagIcon fontSize="small" style={{ color: "#FF8B00" }} /> },
  { id: 8, name: "P2", icon: <FlagIcon fontSize="small" style={{ color: "#FFAB00" }} /> },
  { id: 9, name: "P3", icon: <FlagIcon fontSize="small" style={{ color: "#36B37E" }} /> },
  { id: 10, name: "P4", icon: <FlagIcon fontSize="small" style={{ color: "#57D9A3" }} /> },
  { id: 11, name: "P5", icon: <FlagIcon fontSize="small" style={{ color: "#FF8B00" }} /> },
]

const statuses = [
  { id: 1, name: "TO DO", color: "#ede54e", textColor: "#172B4D" },
  { id: 2, name: "IN PROGRESS", color: "#1972f7", textColor: "#FFFFFF" },
  { id: 3, name: "DONE", color: "#049c02", textColor: "#FFFFFF" },
  { id: 4, name: "ON HOLD", color: "#FFAB00", textColor: "#172B4D" },
  { id: 5, name: "ISSUE CLOSED", color: "#00C7BE", textColor: "#172B4D" },
  { id: 12, name: "BACKLOG", color: "#FF8B00", textColor: "#FFFFFF" },
  { id: 13, name: "PLANNING", color: "#4C9AFF", textColor: "#FFFFFF" },
  { id: 14, name: "NEW", color: "#4C9AFF", textColor: "#FFFFFF" },
]
  // Debug mode to see what's happening with API calls
  const DEBUG = true;

  useEffect(() => {
    if (open && projectId) {
      loadProjectDetails();
    }
  }, [open, projectId]);

  useEffect(() => {
    // Automatically set completion to 100% when status is Completed
    if (status === 'Completed') {
      setCompletionPercentage(100);
    }
  }, [status]);

  // UPDATED: Main function to load all project data
  const loadProjectDetails = async () => {
    if (!projectId) {
      setError("No project ID provided");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      
      // NEW: Extract company_id from token (same as AllProjects)
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error("No authentication token found. Please log in.");
      }
      const payload = JSON.parse(atob(token.split('.')[1]));
      const companyId = payload.company_id;
      if (!companyId) {
        throw new Error("No company_id in token. Please log in again.");
      }
      
      // FIXED: Load project first (pass companyId for fallback)
      await loadProjectData(companyId);
      
      // REMOVED: The failing check `if (!project || !project.company_id)` - now company_id is guaranteed via fallback
      
      // Now load team and change requests in parallel with companyId from token
      const [cmpRes] = await Promise.all([
        axios.get(`${API_URL}/api/cmp/${companyId}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
        loadTeamData(projectId, companyId),
        loadChangeRequestsData(projectId, companyId)
      ]);
      
      if (cmpRes?.data?.data) {
        setCompanyConfig(cmpRes.data.data);
      }
      
    } catch (error) {
      //console.error("Error in loadProjectDetails:", error);
      setError(
        error.response 
          ? `Error ${error.response.status}: ${error.response.data?.message || 'Failed to load project details'}`
          : error.message || "Network error or server not reachable"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = () => {
    const docPath = project?.uploadDocument;
    if (!docPath) return;
    
    const url = docPath.startsWith('http') ? docPath : `${API_URL}/api/projects/${projectId}/document`;
    const ext = docPath.split('.').pop().toLowerCase().split('?')[0];
    
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) {
      window.open(`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`, '_blank');
    } else {
      window.open(url, '_blank');
    }
  };
  
  // UPDATED: handleDownloadDocument
  const handleDownloadDocument = () => {
    // NEW: Append company_id for API consistency (adjust if backend doesn't need it)
    const token = localStorage.getItem('token');
    const companyId = token ? JSON.parse(atob(token.split('.')[1])).company_id : '';
    const downloadUrl = `${API_URL}/api/projects/${projectId}/document?company_id=${companyId}`;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', project?.uploadDocument || 'project-document');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  

  // UPDATED: Separate function for loading project data (now accepts companyId)
  const loadProjectData = async (companyId) => {  // NEW: Accept companyId param
    try {
      
      const projectResponse = await axios.get(`${API_URL}/api/projects/${projectId}`,{
        headers:{
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      // Handle different API response formats
      let projectData;
      if (projectResponse.data.data) {
        projectData = projectResponse.data.data;
      } else {
        projectData = projectResponse.data;
      }
      
      // Process project data with fallbacks
      const processedProject = {
        ...projectData,
        name: projectData.name || "Untitled Project",
        key: projectData.key || "UNKNOWN",
        projectType: projectData.projectType || projectData.type || "Not specified",
        status: projectData.status || "Active",
        description: projectData.description || "No description available",
        startDate: projectData.startDate || projectData.start_date || new Date().toISOString(),
        endDate: projectData.endDate || projectData.end_date || new Date().toISOString(),
        budget: projectData.budget || 0,
        completionPercentage: projectData.completionPercentage || 0,
        company_id: projectData.company_id || companyId  // FIXED: Fallback to token's companyId
      };
      
      // Convert backend status to frontend status for display
      let frontendStatus;
      if (processedProject.status === 'Active') {
        frontendStatus = processedProject.completionPercentage > 0 ? 'In Progress' : 'Not Started';
      } else {
        frontendStatus = backendToFrontendStatus[processedProject.status] || processedProject.status;
      }
      
      processedProject.displayStatus = frontendStatus;
      
      setProject(processedProject);
      
      // Initialize status update form with current values
      setStatus(frontendStatus);
      setCompletionPercentage(processedProject.completionPercentage);
      
    } catch (error) {
      //console.error("Error loading project data:", error);
      throw error;
    }
  };

  // FIXED: Enhanced team data loading with company_id support and multiple endpoint attempts
  const loadTeamData = async (projectId, companyId) => {
    try {
      
      const token = localStorage.getItem("token");

      let teamData = [];
      
      const endpoints = [
        `${API_URL}/api/projects/${projectId}/team`,
        `${API_URL}/api/team/project/${projectId}`,
        `${API_URL}/api/addteam/project/${projectId}`,
        `${API_URL}/api/projects/${projectId}/members`
      ];
      
      for (const endpoint of endpoints) {
        try {
          const teamResponse = await axios.get(endpoint,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );

          
          let responseData;
          if (teamResponse.data.data) {
            responseData = teamResponse.data.data;
          } else if (teamResponse.data.teamMembers) {
            responseData = teamResponse.data.teamMembers;
          } else if (Array.isArray(teamResponse.data)) {
            responseData = teamResponse.data;
          } else {
            responseData = teamResponse.data.team || teamResponse.data.members || [];
          }
          
          if (Array.isArray(responseData) && responseData.length > 0) {
            teamData = responseData.map(member => ({
              id: member.employee_id || member.id || member.userId || member.team_member_id || '',
              employeeId: member.employee_id || member.id || member.userId || member.team_member_id || '',
              name: member.name || member.employeeName || (member.firstName ? `${member.firstName} ${member.lastName || ''}`.trim() : '') || member.employee_id || 'Unknown',
              role: member.role || member.designation || 'Team Member',
              isProjectLead: member.is_project_lead || member.is_product_owner || member.isProjectLead || (project?.lead_id === (member.employee_id || member.id)) || false,
              allocation: member.allocation_percentage || member.allocation || 100,
              startDate: member.start_date || member.startDate,
              endDate: member.end_date || member.endDate
            }));
            break;
          }
        } catch (endpointError) {
          continue;
        }
      }
      
      setTeam(teamData);
      
    } catch (error) {
      //console.error("Error in loadTeamData:", error);
      setTeam([]);
    }
  };

  // FIXED: Enhanced change requests data loading - PROJECT SPECIFIC ONLY, with company_id
const loadChangeRequestsData = async (projectId, companyId) => {
  try {
    const token = localStorage.getItem("token");
    let changeRequestData = [];
    
    // Try multiple endpoint patterns that should filter by project
    const endpoints = [
      `${API_URL}/api/issues/project/${projectId}`, // Most specific endpoint
      `${API_URL}/api/change-requests/project/${projectId}`,
      `${API_URL}/api/projects/${projectId}/issues`,
      `${API_URL}/api/projects/${projectId}/change-requests`,
      `${API_URL}/api/issues?projectId=${projectId}`,
      `${API_URL}/api/issues?project_id=${projectId}`,
      `${API_URL}/api/issues/hierarchy/project/${projectId}`,
    ];
    
    for (const endpoint of endpoints) {
      try {
        const response = await axios.get(endpoint,{
          
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        let responseData;
        if (response.data.data) {
          responseData = response.data.data;
        } else if (response.data.issues) {
          responseData = response.data.issues;
        } else if (response.data.changeRequests) {
          responseData = response.data.changeRequests;
        } else if (Array.isArray(response.data)) {
          responseData = response.data;
        } else {
          responseData = [];
        }
        
        if (Array.isArray(responseData)) {
          // CRITICAL: Filter by project ID to ensure we only get project-specific data
          const filteredData = responseData.filter(item => {
            const itemProjectId = item.project_id || item.projectId || item.project;
            // Convert both to strings for comparison to handle different data types
            const matches = String(itemProjectId) === String(projectId);
         
            return matches;
          });
          
   
          if (filteredData.length > 0) {
            changeRequestData = filteredData.map(item => ({
              id: item.id || item.issueId || item.changeRequestId,
              issueNumber: item.issue_number || item.number || item.id,
              title: item.title || item.summary || item.name || 'Untitled Change Request',
              description: item.description || '',
              status: item.status || 'Pending',
              priority: item.priority || 'Medium',
              type: item.issue_type || item.type || 'Change Request',
              assignee: item.assignee_id || item.assignee || 'Unassigned',
              assigneeName: item.assigneeName ||'Unassigned',
              reporter: item.reporter_id || item.reporter || 'Unknown',
              createdAt: item.created_at || item.createdAt || item.created,
              updatedAt: item.updated_at || item.updatedAt || item.updated,
              dueDate: item.due_date || item.dueDate,
              projectId: item.project_id || item.projectId || projectId // Ensure projectId is set
            }));
            break;
          }
        }
      } catch (endpointError) {
        continue;
      }
    }
    
    // If no project-specific endpoint worked, try the general endpoint with manual filtering
    if (changeRequestData.length === 0) {
      const token = localStorage.getItem("token");
      try {
        const response = await axios.get(`${API_URL}/api/issues`,
          {  headers: {
            Authorization: `Bearer ${token}`
          }}
        );
        let allIssues = [];
        
        if (response.data.data) {
          allIssues = response.data.data;
        } else if (response.data.issues) {
          allIssues = response.data.issues;
        } else if (Array.isArray(response.data)) {
          allIssues = response.data;
        }
        
        if (Array.isArray(allIssues)) {
          // Filter for this specific project only
          const projectSpecificIssues = allIssues.filter(item => {
            const itemProjectId = item.project_id || item.projectId || item.project;
            return String(itemProjectId) === String(projectId);
          });
          

          
          changeRequestData = projectSpecificIssues.map(item => ({
            id: item.id || item.issueId || item.changeRequestId,
            issueNumber: item.issue_number || item.number || item.id,
            title: item.title || item.summary || item.name || 'Untitled Change Request',
            description: item.description || '',
            status: item.status || 'Pending',
            priority: item.priority || 'Medium',
            type: item.issue_type || item.type || 'Change Request',
            assignee: item.assignee_id || item.assignee || 'Unassigned',
            reporter: item.reporter_id || item.reporter || 'Unknown',
            createdAt: item.created_at || item.createdAt || item.created,
            updatedAt: item.updated_at || item.updatedAt || item.updated,
            dueDate: item.due_date || item.dueDate,
            projectId: item.project_id || item.projectId || projectId
          }));
        }
      } catch (generalError) {
      }
    }
    
    // Double-check: ensure all loaded data belongs to this project
    changeRequestData = changeRequestData.filter(item => {
      const itemProjectId = item.projectId;
      return String(itemProjectId) === String(projectId);
    });
    

    
    setChangeRequests(changeRequestData);
    
    // Calculate change request statistics for this project only
    if (changeRequestData.length > 0) {
      const total = changeRequestData.length;
      const approved = changeRequestData.filter(cr => 
        ['Approved', 'approved'].includes(cr.status)
      ).length;
      const pending = changeRequestData.filter(cr => 
        ['Pending', 'pending', 'Open', 'open'].includes(cr.status)
      ).length;
      const rejected = changeRequestData.filter(cr => 
        ['Rejected', 'rejected', 'Declined', 'declined'].includes(cr.status)
      ).length;
      const inReview = changeRequestData.filter(cr => 
        ['In Review', 'in review', 'Under Review', 'Reviewing'].includes(cr.status)
      ).length;
      const implemented = changeRequestData.filter(cr => 
        ['Implemented', 'implemented', 'Completed', 'Done', 'Closed'].includes(cr.status)
      ).length;
      
      const calculatedStats = {
        total,
        approved,
        pending,
        rejected,
        inReview,
        implemented
      };
      
      setChangeRequestStats(calculatedStats);
    } else {
      setChangeRequestStats({
        total: 0,
        approved: 0,
        pending: 0,
        rejected: 0,
        inReview: 0,
        implemented: 0
      });
    }
    
  } catch (error) {
    //console.error("Error in loadChangeRequestsData:", error);
    setChangeRequests([]);
    setChangeRequestStats({
      total: 0,
      approved: 0,
      pending: 0,
      rejected: 0,
      inReview: 0,
      implemented: 0
    });
  }
};
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return "Invalid date";
    }
  };

  // Refresh data function
  const refreshData = async () => {
    await loadProjectDetails();
  };

  // Open status update dialog
  const openUpdateStatusDialog = () => {
    if (project) {
      setStatus(project.displayStatus);
      setCompletionPercentage(project.completionPercentage || 0);
      setNotes('');
      setUpdateDialogOpen(true);
    }
  };

  // Close status update dialog
  const closeUpdateStatusDialog = () => {
    setUpdateDialogOpen(false);
  };

  // Handle status update submission
  const handleStatusUpdate = async () => {
    try {
      setUpdateLoading(true);
      
      const backendStatus = frontendToBackendStatus[status];
      const updateData = {
        status: backendStatus,
        completionPercentage: parseInt(completionPercentage),
        notes: notes
      };
      
      
      await axios.put(`${API_URL}/api/projects/${projectId}`, updateData,{
        headers:{
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      setProject(prev => ({
        ...prev,
        status: backendStatus,
        displayStatus: status,
        completionPercentage: parseInt(completionPercentage)
      }));
      
      setNotification({
        open: true,
        message: 'Project status updated successfully!',
        type: 'success'
      });
      
      setUpdateDialogOpen(false);
    } catch (error) {
      //console.error('Error updating project status:', error);
      setNotification({
        open: true,
        message: 'Failed to update project status. Please try again.',
        type: 'error'
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Loading Project Details...</Typography>
            <IconButton aria-label="close" onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Error</Typography>
            <IconButton aria-label="close" onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
            <Button variant="contained" onClick={loadProjectDetails}>
              Retry
            </Button>
            <Button variant="outlined" onClick={refreshData} startIcon={<RefreshIcon />}>
              Refresh
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!project) {
    return null;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">{project.name}</Typography>
              <Chip 
                label={project.key} 
                size="small" 
                sx={{ ml: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }} 
              />
              <ProjectStatusChip status={project.displayStatus} />
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton onClick={refreshData} size="small" title="Refresh data">
                <RefreshIcon />
              </IconButton>
              <IconButton aria-label="close" onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange} aria-label="project tabs"   variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile>
            <Tab icon={<BusinessIcon />} iconPosition="start" label="Overview" />
            <Tab icon={<GroupIcon />} iconPosition="start" label={`Team (${team.length})`} />
            <Tab icon={<ChangeRequestIcon />} iconPosition="start" label={`Requirements (${changeRequests.length})`} />
            <Tab icon={<ProgressIcon />} iconPosition="start" label="Progress" />
          </Tabs>
        </Box>
        
        {/* Overview Tab */}
        <TabPanel value={value} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>Project Description</Typography>
                <Typography variant="body1" paragraph>
                  {project.description}
                </Typography>
                
                {/* Document Section - ADDED */}
                {project.uploadDocument && (
  <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
    <Typography variant="h6" gutterBottom>Project Document</Typography>
    <Box display="flex" alignItems="center">
      <Description sx={{ mr: 1, color: 'primary.main' }} />
      <Typography variant="body1" sx={{ flexGrow: 1, wordBreak: 'break-all' }}>
        {project.uploadDocument.split('/').pop().split('-').slice(1).join('-') || project.uploadDocument.split('/').pop()}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button 
          variant="outlined" 
          startIcon={<VisibilityIcon />}
          onClick={handleViewDocument}
        >
          View
        </Button>
        <Button 
          variant="contained" 
          startIcon={<DownloadIcon />}
          onClick={handleDownloadDocument}
        >
          Download
        </Button>
      </Box>
    </Box>
  </Box>
)}
                
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Project Details</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Start Date</Typography>
                        <Typography variant="body2">
                          {formatDate(project.startDate || project.start_date)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">End Date</Typography>
                        <Typography variant="body2">
                          {formatDate(project.endDate || project.end_date)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Project Type</Typography>
                        <Typography variant="body2">{project.projectType || project.project_type || "Not specified"}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <CurrencyRupee sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Budget</Typography>
                        <Typography variant="body2">
                          {project.budget !== undefined ? 
                            `${companyConfig?.currency_symbol || '₹'}${typeof project.budget === 'number' ? project.budget.toLocaleString() : project.budget}` : 
                            "Not specified"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>Project Progress</Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Overall Completion</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {project.completionPercentage || 0}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={project.completionPercentage || 0} 
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
                
                <Box>
                  <Typography variant="body2">Project Status</Typography>
                  <Box sx={{ mt: 1 }}>
                    <ProjectStatusChip status={project.displayStatus || "Not specified"} />
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* Team Tab */}
        <TabPanel value={value} index={1}>
          {team.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Team Members Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This project doesn't have any team members assigned yet.
              </Typography>
              <Button 
                variant="outlined" 
                sx={{ mt: 2 }}
                onClick={() => {
                  // FIXED: Extract from token if project.company_id missing
                  const token = localStorage.getItem('token');
                  const tokenCompanyId = token ? JSON.parse(atob(token.split('.')[1])).company_id : null;
                  loadTeamData(projectId, project?.company_id || tokenCompanyId);
                }}
                startIcon={<RefreshIcon />}
              >
                Retry Loading Team
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Team Member</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Allocation</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Start Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>End Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {team.map((member, index) => (
                    <TableRow key={member.employeeId || member.id || index}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                            {(member.name || member.employeeId || "?").charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2">
                              {member.name || member.employeeId || "Unknown"}
                            </Typography>
                            {member.isProjectLead && (
                              <Chip label="Project Lead" size="small" color="primary" sx={{ mt: 0.5 }} />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{member.role || "Not specified"}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: '100%', mr: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={member.allocation || 0} 
                              sx={{ height: 6, borderRadius: 3 }} 
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {member.allocation || 0}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(member.startDate)}</TableCell>
                      <TableCell>{formatDate(member.endDate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      
        {/* Change Requests Tab - NEW */}
        <TabPanel value={value} index={2}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Requirements</Typography>
           
          </Box>
         
          
          {changeRequests.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ChangeRequestIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Change Requests Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This project doesn't have any change requests yet.
              </Typography>
              <Button 
                variant="outlined" 
                sx={{ mt: 2 }}
                onClick={() => {
                  // FIXED: Extract from token if project.company_id missing
                  const token = localStorage.getItem('token');
                  const tokenCompanyId = token ? JSON.parse(atob(token.split('.')[1])).company_id : null;
                  loadChangeRequestsData(projectId, project?.company_id || tokenCompanyId);
                }}
                startIcon={<RefreshIcon />}
              >
                Try Loading Change Requests Again
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Priority</TableCell>
                    {/* <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell> */}
                    <TableCell sx={{ fontWeight: 'bold' }}>Assignee</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                    {/* <TableCell sx={{ fontWeight: 'bold' }}>Due Date</TableCell> */}
                    
                  </TableRow>
                </TableHead>
                <TableBody>
                  {changeRequests.map((request, index) => (
                    <TableRow key={request.id || index}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          #{request.issueNumber || request.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {request.title}
                          </Typography>
                          {request.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              {request.description.length > 50 
                                ? `${request.description.substring(0, 50)}...` 
                                : request.description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    <TableCell>
  {statuses.find(s => s.id === request.status)?.name || request.status}
</TableCell>
<TableCell>
  <Chip 
    label={priorities.find(p => p.id === request.priority)?.name || request.priority}
    size="small" 
    color={
      request.priority === 1 || request.priority === 2 ? 'error' :  // Highest, High, P1, P5
      request.priority === 3 || request.priority === 7 || request.priority === 8 ? 'warning' : // Medium, P2, P3
      'default' // Low, Lowest, P4, Normal
    }
  />
</TableCell>
                      {/* <TableCell>
                        <Typography variant="body2">{request.type}</Typography>
                      </TableCell> */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: 12 }}>
                            {(request.assigneeName || "U").charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2">
                            {request.assigneeName || "Unassigned"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(request.createdAt)}
                        </Typography>
                      </TableCell>
                      {/* <TableCell>
                        <Typography variant="body2">
                          {formatDate(request.dueDate)}
                        </Typography>
                      </TableCell> */}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
      
        </TabPanel>
        
        {/* Progress Tab */}
        <TabPanel value={value} index={3}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Project Progress</Typography>
          
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Overall Progress</Typography>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Completion Percentage</Typography>
                    <Typography variant="h6" color="primary">
                      {project.completionPercentage || 0}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={project.completionPercentage || 0} 
                    sx={{ height: 12, borderRadius: 6 }}
                  />
                </Box>
                
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" gutterBottom>Current Status</Typography>
                  <ProjectStatusChip status={project.displayStatus} />
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Project Timeline</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Start Date</Typography>
                    <Typography variant="body1">
                      {formatDate(project.startDate)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">End Date</Typography>
                    <Typography variant="body1">
                      {formatDate(project.endDate)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Duration</Typography>
                    <Typography variant="body1">
                      {project.startDate && project.endDate ? 
                        `${Math.ceil((new Date(project.endDate) - new Date(project.startDate)) / (1000 * 60 * 60 * 24))} days` :
                        "Not calculated"
                      }
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Team & Resources</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <GroupIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                      <Typography variant="h4" color="primary">
                        {team.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Team Members
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <TaskIcon sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
                      <Typography variant="h4" color="warning.main">
                        {changeRequests.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Requirements
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center' }}>
                    <IndianRupeeIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />

                      <Typography variant="h4" color="success.main">
                        {companyConfig?.currency_symbol || '₹'}{project.budget ? (typeof project.budget === 'number' ? 
                          (project.budget / 100000).toFixed(1) : 
                          project.budget) : '0'}L
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Budget (Lakhs)
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
      </Dialog>
      
  
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.type}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProjectDetailsDialog;